// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { D3eService } from 'src/services/d3e-service';
import { HeaderService } from 'src/app/header.service';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { IIntegration, PartnerTypes } from 'src/types/integrations';
import { NotificationService } from 'src/services/notification-service';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import {SelectedOrgMethod} from "../../../../shared/selectedOrg";
import {TranslateService} from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-cisco-duo',
    templateUrl: './cisco-duo.component.html',
    styleUrls: ['./cisco-duo.component.scss'],
    standalone: false
})
export class CiscoDuoComponent implements OnInit {
  private readonly PARTNER_ID = PartnerTypes.CISCO_DUO;
  formGroup: UntypedFormGroup;
  integration: IIntegration;
  integrationId: string = null;

  selectedOrgId: string;
  hasUserBeenSet = false;

  fSecret: string;
  fInt: string;
  fHost: string;
  fSave: string;
  fCancel: string;
  fConfigSaved: string;
  fChangesBeingSaved: string;
  fSaveError: string;

  title: string;
  subtitle: string;
  constructor(
    private route: ActivatedRoute,
    private d3eService: D3eService,
    private router: Router,
    private headerService: HeaderService,
    private formBuilder: UntypedFormBuilder,
    private notification: NotificationService,
    public dialog: MatDialog,
    private translate: TranslateService
  ) {
    this.translate.get(['INTCD_SECRET', 'INTCD_INT', 'INTCD_HOST', 'SAVE', 'CANCEL', 'TITLE_CISCOD', 'SUBTITLE_CISCOD',
      'CONFIG_SAVED', 'CHANGES_BEING_SAVED', 'REPORT_SAVE_ERROR'])
        .subscribe(vals => {
      this.fSecret = vals['INTCD_SECRET'];
      this.fInt = vals['INTCD_INT'];
      this.fHost = vals['INTCD_HOST'];
      this.fSave = vals['SAVE'];
      this.fCancel = vals['CANCEL'];
      this.fChangesBeingSaved = vals['CHANGES_BEING_SAVED'];

      this.title = vals['TITLE_CISCOD_INT'];
      this.subtitle = vals['SUBTITLE_CISCOD'];
      this.fConfigSaved = vals['CONFIG_SAVED'];
      this.fSaveError = vals['REPORT_SAVE_ERROR'];

      this.headerService.setTitle(this.title);
      this.headerService.setSubtitle(this.subtitle);
      this.headerService.setContextHelp(['help.integrations.cisco-duo.title']);
    });
    this.headerService.setIcon('mdi-sync');

    this.formGroup = this.formBuilder.group({
      secret_key: ['', Validators.required],
      integration_key: ['', Validators.required],
      api_hostname: ['', Validators.required],
    });
  }

  ngOnInit() {

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
            // TODO: launch the dirty flag window
            this.router.navigateByUrl('/integrations');
        }
        this.hasUserBeenSet = true;
    });

    this.route.params.subscribe(params => {
      this.integrationId = params.id;
      if (this.integrationId !== 'new') {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.get_integration(this.integrationId, selectedOrg)
          .pipe(untilDestroyed(this))
          .subscribe(integration => {
            if (integration.partner_id !== this.PARTNER_ID) {
              this.router.navigateByUrl('/integrations');
            }
            this.formGroup.controls['secret_key'].setValue(integration.config.secret_key);
            this.formGroup.controls['integration_key'].setValue(integration.config.integration_key);
            this.formGroup.controls['api_hostname'].setValue(integration.config.api_hostname);
            this.integration = integration;
        });
      }
    });
  }

  save() {
    // this will make the button become disabled right away to keep a double click from adding two
    document.getElementById('save').setAttribute('disabled', 'true');

    this.notification.showUpdate(this.fChangesBeingSaved);
    const data = this.formGroup.value;
    if (this.integrationId === 'new') {
      const integration: IIntegration = {
        display_name: 'Cisco DUO',
        enabled: false,
        partner_id: PartnerTypes.CISCO_DUO,
        config: {
          login_url: data.login_url,
          integration_key: data.integration_key,
          secret_key: data.secret_key,
          api_hostname: data.api_hostname,
        }
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.add_integration(integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    } else {
      this.integration.config = {
        login_url: data.login_url,
        integration_key: data.integration_key,
        secret_key: data.secret_key,
        api_hostname: data.api_hostname,
    };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.update_integration(this.integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    }
  }

  reportSaveSuccess() {
    this.notification.showSuccess(this.fConfigSaved);
  }

  reportSaveError() {
    this.notification.showError(this.fSaveError);
  }

  goToIntegrations() {
    this.router.navigateByUrl('/integrations');
  }

  cancel() {
    if (this.formGroup.touched) {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: this.translate.instant('DISCARD_CHANGES'),
          content: this.translate.instant('DISCARD_CHANGES_CONTENT'),
          rejectBtnName: this.translate.instant('NO'),
          acceptBtnName: this.translate.instant('YES'),
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.goToIntegrations();
        }
      });
    } else {
      this.goToIntegrations();
    }
  }

  validate() {
    const data = this.formGroup.value;
    // TODO: DO REAL VALIDATION
    //     this.notification.showError('Validation Failed', 'error: ' + response.error, 10000);
    //   } else if (response.status_code && response.status_code !== 200) {
    //     this.notification.showError('Validation Failed', 'error: HTTP Status code ' + response.status_code, 10000);
    //   } else if (response.status_code && response.status_code === 200) {
    //     this.notification.showSuccess('Validation Succeeded', 'Configuration appears to be correct', 3000);
    //   }
    // });
  }

}
